import axios from "axios";
import { reverse, sortBy } from 'lodash';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { fetchCurrentUser } from '../../ducks/user.duck';
import { apiBaseUrl, handleDealsOnCloseListing, onFetchAnalytics } from '../../util/api';
import { denormalisedEntities, updatedEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import { createImageVariantConfig } from '../../util/sdkLoader';
import { DEAL_LISTING_TYPE, PRODUCT_LISTING_TYPE, SERVICE_LISTING_TYPE, STAFF_LISTING_TYPE } from '../../util/types';
import { parse } from '../../util/urlHelpers';
import { createListingInAlgolia } from "../EditListingPage/EditListingPage.duck";
import moment from "moment";

// Pagination page size might need to be dynamic on responsive page layouts
// Current design has max 3 columns 42 is divisible by 2 and 3
// So, there's enough cards to fill all columns on full pagination pages
const RESULT_PAGE_SIZE = 42;

// ================ Action types ================ //

export const FETCH_LISTINGS_REQUEST = 'app/ViewListingsPage/FETCH_LISTINGS_REQUEST';
export const FETCH_LISTINGS_SUCCESS = 'app/ViewListingsPage/FETCH_LISTINGS_SUCCESS';
export const FETCH_LISTINGS_ERROR = 'app/ViewListingsPage/FETCH_LISTINGS_ERROR';

export const OPEN_LISTING_REQUEST = 'app/ViewListingsPage/OPEN_LISTING_REQUEST';
export const OPEN_LISTING_SUCCESS = 'app/ViewListingsPage/OPEN_LISTING_SUCCESS';
export const OPEN_LISTING_ERROR = 'app/ViewListingsPage/OPEN_LISTING_ERROR';

export const CLOSE_LISTING_REQUEST = 'app/ViewListingsPage/CLOSE_LISTING_REQUEST';
export const CLOSE_LISTING_SUCCESS = 'app/ViewListingsPage/CLOSE_LISTING_SUCCESS';
export const CLOSE_LISTING_ERROR = 'app/ViewListingsPage/CLOSE_LISTING_ERROR';

export const ADD_OWN_ENTITIES = 'app/ViewListingsPage/ADD_OWN_ENTITIES';


export const FETCH_ORDERS_OR_SALES_REQUEST = 'app/ViewListingsPage/FETCH_ORDERS_OR_SALES_REQUEST';
export const FETCH_ORDERS_OR_SALES_SUCCESS = 'app/ViewListingsPage/FETCH_ORDERS_OR_SALES_SUCCESS';
export const FETCH_ORDERS_OR_SALES_ERROR = 'app/ViewListingsPage/FETCH_ORDERS_OR_SALES_ERROR';

export const FETCH_ANALYTICS_DATA_REQUEST = 'app/ViewListingsPage/FETCH_ANALYTICS_DATA_REQUEST';
export const FETCH_ANALYTICS_DATA_SUCCESS = 'app/ViewListingsPage/FETCH_ANALYTICS_DATA_SUCCESS';
export const FETCH_ANALYTICS_DATA_ERROR = 'app/ViewListingsPage/FETCH_ANALYTICS_DATA_ERROR';

export const DOWNLOAD_REPORT_REQUEST = 'app/ViewListingsPage/DOWNLOAD_REPORT_REQUEST';
export const DOWNLOAD_REPORT_SUCCESS = 'app/ViewListingsPage/DOWNLOAD_REPORT_SUCCESS';
export const DOWNLOAD_REPORT_ERROR = 'app/ViewListingsPage/DOWNLOAD_REPORT_ERROR';

// ================ Reducer ================ //
const entityRefs = entities =>
  entities.map(entity => ({
    id: entity.id,
    type: entity.type,
  }));



const initialState = {
  pagination: null,
  queryParams: null,
  queryInProgress: false,
  queryListingsError: null,
  currentPageResultIds: [],
  ownEntities: {},
  transactionRefs: [],
  openingListing: null,
  openingListingError: null,
  closingListing: null,
  closingListingError: null,
  fetchAnalyticsRequest: false,
  fetchAnalyticsSuccess: null,
  fetchAnalyticsError: null,
  downloadReportRequest: false,
  downloadReportSuccess: null,
  downloadReportError: null,
};

const resultIds = data => data.data.map(l => l.id);

const merge = (state, sdkResponse) => {
  const apiResponse = sdkResponse.data;
  return {
    ...state,
    ownEntities: updatedEntities({ ...state.ownEntities }, apiResponse),
  };
};

const sortedTransactions = txs =>
  reverse(
    sortBy(txs, tx => {
      return tx.attributes ? tx.attributes.lastTransitionedAt : null;
    })
  );


const updateListingAttributes = (state, listingEntity) => {
  const oldListing = state.ownEntities.ownListing[listingEntity.id.uuid];
  const updatedListing = { ...oldListing, attributes: listingEntity.attributes };
  const ownListingEntities = {
    ...state.ownEntities.ownListing,
    [listingEntity.id.uuid]: updatedListing,
  };
  return {
    ...state,
    ownEntities: { ...state.ownEntities, ownListing: ownListingEntities },
  };
};

const viewListingsPageReducer = (state = initialState, action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case FETCH_LISTINGS_REQUEST:
      return {
        ...state,
        queryParams: payload.queryParams,
        queryInProgress: true,
        queryListingsError: null,
        currentPageResultIds: [],
      };
    case FETCH_LISTINGS_SUCCESS:
      return {
        ...state,
        currentPageResultIds: resultIds(payload.data),
        pagination: payload.data.meta,
        queryInProgress: false,
      };
    case FETCH_LISTINGS_ERROR:
      // eslint-disable-next-line no-console
      console.error(payload);
      return { ...state, queryInProgress: false, queryListingsError: payload };

    case OPEN_LISTING_REQUEST:
      return {
        ...state,
        openingListing: payload.listingId,
        openingListingError: null,
      };
    case OPEN_LISTING_SUCCESS:
      return {
        ...updateListingAttributes(state, payload.data),
        openingListing: null,
      };
    case OPEN_LISTING_ERROR: {
      // eslint-disable-next-line no-console
      console.error(payload);
      return {
        ...state,
        openingListing: null,
        openingListingError: {
          listingId: state.openingListing,
          error: payload,
        },
      };
    }

    case CLOSE_LISTING_REQUEST:
      return {
        ...state,
        closingListing: payload.listingId,
        closingListingError: null,
      };
    case CLOSE_LISTING_SUCCESS:
      return {
        ...updateListingAttributes(state, payload.data),
        closingListing: null,
      };
    case CLOSE_LISTING_ERROR: {
      // eslint-disable-next-line no-console
      console.error(payload);
      return {
        ...state,
        closingListing: null,
        closingListingError: {
          listingId: state.closingListing,
          error: payload,
        },
      };
    }

    case FETCH_ORDERS_OR_SALES_REQUEST:
      return { ...state, fetchInProgress: true, fetchOrdersOrSalesError: null };
    case FETCH_ORDERS_OR_SALES_SUCCESS: {
      const transactions = sortedTransactions(payload.data.data);
      return {
        ...state,
        fetchInProgress: false,
        transactionRefs: entityRefs(transactions),
        pagination: payload.data.meta,
      };
    }
    case FETCH_ORDERS_OR_SALES_ERROR:
      console.error(payload);
      return { ...state, fetchInProgress: false, fetchOrdersOrSalesError: payload };


    case FETCH_ANALYTICS_DATA_REQUEST:
      return {
        ...state,
        fetchAnalyticsRequest: true,
        fetchAnalyticsSuccess: null,
      };
    case FETCH_ANALYTICS_DATA_SUCCESS:
      return {
        ...state,
        fetchAnalyticsRequest: false,
        fetchAnalyticsSuccess: payload,
      };

    case FETCH_ANALYTICS_DATA_ERROR:
      return {
        ...state,
        fetchAnalyticsRequest: false,
        fetchAnalyticsSuccess: null,
        fetchAnalyticsError: payload,
      };

    case DOWNLOAD_REPORT_REQUEST:
      return {
        ...state,
        downloadReportRequest: true,
        downloadReportSuccess: null,
      };
    case DOWNLOAD_REPORT_SUCCESS:
      return {
        ...state,
        downloadReportRequest: false,
        downloadReportSuccess: payload,
      };

    case DOWNLOAD_REPORT_ERROR:
      return {
        ...state,
        downloadReportRequest: false,
        downloadReportSuccess: null,
        downloadReportError: payload,
      };

    case ADD_OWN_ENTITIES:
      return merge(state, payload);

    default:
      return state;
  }
};

export default viewListingsPageReducer;

// ================ Selectors ================ //

/**
 * Get the denormalised own listing entities with the given IDs
 *
 * @param {Object} state the full Redux store
 * @param {Array<UUID>} listingIds listing IDs to select from the store
 */
export const getOwnListingsById = (state, listingIds) => {
  const { ownEntities } = state.ViewListingsPage;
  const resources = listingIds.map(id => ({
    id,
    type: 'ownListing',
  }));
  const throwIfNotFound = false;
  return denormalisedEntities(ownEntities, resources, throwIfNotFound);
};

// ================ Action creators ================ //

// This works the same way as addMarketplaceEntities,
// but we don't want to mix own listings with searched listings
// (own listings data contains different info - e.g. exact location etc.)
export const addOwnEntities = sdkResponse => ({
  type: ADD_OWN_ENTITIES,
  payload: sdkResponse,
});

export const openListingRequest = listingId => ({
  type: OPEN_LISTING_REQUEST,
  payload: { listingId },
});

export const openListingSuccess = response => ({
  type: OPEN_LISTING_SUCCESS,
  payload: response.data,
});

export const openListingError = e => ({
  type: OPEN_LISTING_ERROR,
  error: true,
  payload: e,
});

export const closeListingRequest = listingId => ({
  type: CLOSE_LISTING_REQUEST,
  payload: { listingId },
});

export const closeListingSuccess = response => ({
  type: CLOSE_LISTING_SUCCESS,
  payload: response.data,
});

export const closeListingError = e => ({
  type: CLOSE_LISTING_ERROR,
  error: true,
  payload: e,
});

export const queryListingsRequest = queryParams => ({
  type: FETCH_LISTINGS_REQUEST,
  payload: { queryParams },
});

export const queryListingsSuccess = response => ({
  type: FETCH_LISTINGS_SUCCESS,
  payload: { data: response.data },
});

export const queryListingsError = e => ({
  type: FETCH_LISTINGS_ERROR,
  error: true,
  payload: e,
});

const fetchOrdersOrSalesRequest = () => ({ type: FETCH_ORDERS_OR_SALES_REQUEST });
const fetchOrdersOrSalesSuccess = response => ({
  type: FETCH_ORDERS_OR_SALES_SUCCESS,
  payload: response,
});
const fetchOrdersOrSalesError = e => ({
  type: FETCH_ORDERS_OR_SALES_ERROR,
  error: true,
  payload: e,
});

// Fetch Report
export const fetchAnalyticsRequest = () => ({
  type: FETCH_ANALYTICS_DATA_REQUEST,
});

export const fetchAnalyticsSuccess = payload => ({
  type: FETCH_ANALYTICS_DATA_SUCCESS,
  payload: payload,
});

export const fetchAnalyticsError = response => ({
  type: FETCH_ANALYTICS_DATA_ERROR,
  payload: response,
});

// Download Report
export const downloadReportRequest = () => ({
  type: DOWNLOAD_REPORT_REQUEST,
});

export const downloadReportSuccess = payload => ({
  type: DOWNLOAD_REPORT_SUCCESS,
  payload: payload,
});

export const downloadReportError = response => ({
  type: DOWNLOAD_REPORT_ERROR,
  payload: response,
});


// Throwing error for new (loadData may need that info)
export const queryOwnListings = ({ filterParams, params, search, config }) => (dispatch, getState, sdk) => {
  const paginationParams = parse(search);
  const { page = 1 } = paginationParams;
  const { listingType = "" } = params;
  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;
  const queryParams = {
    ...paginationParams,
    page,
    pub_listingType: listingType,
    perPage: RESULT_PAGE_SIZE,
    include: ['images', 'currentStock'],
    'fields.image': [`variants.${variantPrefix}`, `variants.${variantPrefix}-2x`],
    ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
    'limit.images': 1,
    ...filterParams
  }

  dispatch(queryListingsRequest(queryParams));

  const { perPage, ...rest } = queryParams;
  const param = { ...rest, perPage };

  return sdk.ownListings
    .query(param)
    .then(response => {
      dispatch(addOwnEntities(response));
      dispatch(queryListingsSuccess(response));
      return response;
    })
    .catch(e => {
      dispatch(queryListingsError(storableError(e)));
      throw e;
    });
};

export const closeListing = listingId => (dispatch, getState, sdk) => {
  dispatch(closeListingRequest(listingId));
  const { currentUser } = getState().user || {};

  return sdk.ownListings
    .close({ id: listingId }, { expand: true })
    .then(response => {
      dispatch(closeListingSuccess(response));
      createListingInAlgolia(response, currentUser?.id?.uuid);
      handleDealsOnCloseListing({ listingId, action: 'close' }).catch(e => {
        console.error('Failed to auto-close linked deals:', e);
      });
      return response;
    })
    .catch(e => {
      dispatch(closeListingError(storableError(e)));
    });
};

export const openListing = listingId => (dispatch, getState, sdk) => {
  dispatch(openListingRequest(listingId));
  const { currentUser } = getState().user || {};

  return sdk.ownListings
    .open({ id: listingId }, { expand: true })
    .then(response => {
      dispatch(openListingSuccess(response));
      createListingInAlgolia(response, currentUser?.id?.uuid);
      return response;
    })
    .catch(e => {
      dispatch(openListingError(storableError(e)));
    });
};

export const reFetchAnalytics = (payload) => async (dispatch, getState, sdk) => {
  const { providerId, processName, dateRange } = payload
  dispatch(fetchAnalyticsRequest());
  const userTimezone = moment.tz.guess();

  await dispatch(fetchCurrentUser());
  const { currentUser } = getState().user || {};

  onFetchAnalytics({ providerId: currentUser?.id?.uuid, processName, dateRange, userTimezone })
    .then((res) => {
      dispatch(fetchAnalyticsSuccess(res));
    })
    .catch((err) => {
      console.log(err, '### ### => err');
      dispatch(fetchAnalyticsError(err))
    })
}

export const handleDownloadReport = (data) => async (dispatch, getState, sdk) => {
  try {
    dispatch(downloadReportRequest());
    const response = await axios.post(
      `${apiBaseUrl()}/api/download-report`,
      data,
      {
        responseType: 'blob',
        headers: {
          'Content-Type': 'application/transit+json',
        }
      });

    dispatch(downloadReportSuccess(response?.data));
    return response?.data;
  } catch (error) {
    console.error('Error downloading the report:', error);
    dispatch(downloadReportError(error));
  }
};

export const loadData = (params, search, config) => (dispatch, getState, sdk) => {
  const { listingType } = params;
  dispatch(fetchCurrentUser());

  const allProcessNames = {
    [DEAL_LISTING_TYPE]: ["default-deal-booking", "default-deal-purchase"],
    [SERVICE_LISTING_TYPE]: ["default-booking"],
    [PRODUCT_LISTING_TYPE]: ["default-purchase"],
    [STAFF_LISTING_TYPE]: ["default-booking"],
  }

  const queryParams = parse(search);
  const page = queryParams.page || 1;
  dispatch(fetchOrdersOrSalesRequest());

  const apiQueryParams = {
    only: "sale",
    // lastTransitions: getAllTransitionsForEveryProcess(),
    processNames: allProcessNames?.[listingType],
    include: [
      'listing',
      'provider',
      'provider.profileImage',
      'customer',
      'customer.profileImage',
      'booking',
    ],
    'fields.transaction': [
      'processName',
      'lastTransition',
      'lastTransitionedAt',
      'transitions',
      'payinTotal',
      'payoutTotal',
      'lineItems',
    ],
    'fields.listing': ['title', 'availabilityPlan', 'publicData.listingType'],
    'fields.user': ['profile.displayName', 'profile.abbreviatedName'],
    'fields.image': ['variants.square-small', 'variants.square-small2x'],
    page,
  };

  return sdk.transactions
    .query(apiQueryParams)
    .then(response => {
      dispatch(addMarketplaceEntities(response));
      dispatch(fetchOrdersOrSalesSuccess(response));

      let filterParams = {};
      dispatch(queryOwnListings({ filterParams, params, search, config }))

      const { currentUser } = getState().user;
      const { userType } = currentUser?.attributes?.profile?.publicData || {};

      const isProvider = userType === "provider";
      const userId = currentUser?.id?.uuid;

      // if (isProvider && userId) {
      //   dispatch(fetchAnalyticsRequest());

      //   onFetchAnalytics({ providerId: userId, processName: allProcessNames?.[listingType] })
      //     .then((res) => {
      //       dispatch(fetchAnalyticsSuccess(res));
      //     })
      //     .catch((err) => {
      //       console.log(err, '### ### => err');
      //       dispatch(fetchAnalyticsError(err))
      //     })
      // }
      return;
    })
    .catch(e => {
      dispatch(fetchOrdersOrSalesError(storableError(e)));
      throw e;
    });
};